import { DeliveryEnum } from '../interfaces'
import { RangeDeclareDto } from '../interfaces/General/RangelDeclareDto'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../interfaces/Import/ShipmentGroupInterface'

/**
 * Validación de grupos y piezas de importación, extraída de ShipmentBulkUploadReview
 * (deuda TODOS, eng-review E1) para reusarla en el Review y en la validación
 * pre-vuelo (#1). Función pura: la validación de grupo recibe su contexto explícito.
 *
 * Esta es la validación cliente que mirror-ea las reglas conocidas del backend; es la
 * base del pre-vuelo #1. La validez de account_code/mailbox (lookup backend) requiere
 * el endpoint dry-run (dependencia abierta).
 */

export const isValidCoord = (
  val: number | null,
  min: number,
  max: number
): boolean => val !== null && !isNaN(val) && val >= min && val <= max

// ---------------------------------------------------------------------------
// Teléfono: paridad EXACTA con el validador del backend (is_phone_number).
// El backend arma `${countryPhoneAccessCode} ${digits}` (ej "+58 4141234567") y
// exige que matchee ^\+\d{1,3} \d{9,15}$. Como el código de país (+58) es fijo,
// esto se reduce a: los dígitos locales deben ser 9 a 15. Un FIJO de 8 dígitos
// (ej "07124922" → "+58 07124922") REVIENTA en el backend; esta es la causa raíz
// del error "is not a valid phone number" al enviar guías desde un favorito.
// ---------------------------------------------------------------------------
export const BACKEND_PHONE_RE = /^\+\d{1,3} \d{9,15}$/
export const PHONE_COUNTRY_CODE = '+58'

/** Compone el valor tal cual viaja al backend: `${countryCode} ${digits}`. */
export function composeConsigneePhone(
  digits: string | null | undefined,
  countryCode: string = PHONE_COUNTRY_CODE
): string {
  return `${countryCode} ${(digits || '').trim()}`
}

/** True si el teléfono compuesto pasaría el validador del backend. */
export function isBackendValidPhone(
  digits: string | null | undefined,
  countryCode: string = PHONE_COUNTRY_CODE
): boolean {
  return BACKEND_PHONE_RE.test(composeConsigneePhone(digits, countryCode))
}

/**
 * Mínimo valor declarado exigido para un peso+servicio, según la tabla de rangos del
 * sistema (state.inmutable.rangeDeclareList). Paridad con la lógica de shipments/create
 * (CreateShipmentV2.minDeclaredValue): busca el rango del servicio; si no hay rango
 * específico, cae al rango genérico (serviceID === 0). Devuelve 0 si ningún rango aplica.
 */
export function minDeclaredValue(
  weight: number,
  rangeList: RangeDeclareDto[],
  service?: number
): number {
  let filtered = rangeList.filter((r) => r.serviceID === service)
  if (filtered.length === 0) filtered = rangeList.filter((r) => r.serviceID === 0)
  return (
    filtered.find((r) => r.minWeight < weight && weight <= r.maxWeight)
      ?.minDeclareValue ?? 0
  )
}

export interface ValidateGroupContext {
  citiesByBu: any[]
  businessUnits: any[]
  isCOD: boolean
  rangeDeclareList: RangeDeclareDto[]
  valueDeclareServiceIds?: Set<number>
  // Oficinas origen usables del contrato (para validar el origen POR FILA).
  originBusinessUnits?: any[]
}

export function validateGroup(
  g: ShipmentGroup,
  ctx: ValidateGroupContext
): ShipmentGroupErrors {
  const {
    citiesByBu,
    isCOD,
    rangeDeclareList,
    valueDeclareServiceIds,
    originBusinessUnits = [],
  } = ctx
  const e: ShipmentGroupErrors = {}

  // [F1/F2] Origen por fila: solo cuando el contrato tiene VARIAS oficinas hay que
  // elegir. Vacío o buCode no resoluble contra el catálogo → error de fila (nunca
  // fallback silencioso). Con 1 sola oficina se auto-asigna (no se exige).
  if (originBusinessUnits.length > 1) {
    if (!g.originStoreCode?.trim()) {
      e.originStoreCode = 'Selecciona la oficina origen'
    } else if (
      !originBusinessUnits.some((b) => b.buCode === g.originStoreCode)
    ) {
      e.originStoreCode = 'Oficina origen no válida para este contrato'
    }
  }
  if (!g.shipmentHeaderReference?.trim()) e.shipmentHeaderReference = 'Requerido'
  else if (g.shipmentHeaderReference.trim().length > 30)
    // El input manual está capado a 30 (maxLength), pero las guías cargadas desde Excel
    // no pasan por ese cap; se marcan aquí para que el operador las corrija antes de enviar.
    e.shipmentHeaderReference = 'Máximo 30 caracteres'
  if (!g.deliveryType) e.deliveryType = 'Requerido'

  if (g.deliveryType === DeliveryEnum.DELIVERY) {
    if (!g.destinationCity?.trim()) {
      e.destinationCity = 'Requerido para domicilio'
    } else {
      // citiesByBu (Django, GET /api/business-unit-location) ES la tabla autoritativa
      // ciudad→tienda destino. NO cruzar contra businessUnits: basta con que la ciudad
      // tenga BU asignada aquí.
      const cityBuCode = citiesByBu.find(
        (c) => c.locationCode === g.destinationCity
      )?.buCode
      if (!cityBuCode) {
        e.destinationCity = 'Ciudad sin tienda asignada en el sistema'
      }
    }
  }
  if (g.deliveryType === DeliveryEnum.OFFICE && !g.storeCode?.trim())
    e.storeCode = 'Requerido para retiro por oficina'

  if (!isCOD) {
    if (!g.shipperDocType?.trim()) e.shipperDocType = 'Requerido'
    if (!g.shipperDocNumber?.trim()) e.shipperDocNumber = 'Requerido'
    if (!g.shipperName?.trim()) e.shipperName = 'Requerido'
  }

  if (!g.consigneeDocType?.trim()) e.consigneeDocType = 'Requerido'
  if (!g.consigneeDocNumber?.trim()) e.consigneeDocNumber = 'Requerido'
  if (!g.consigneeName?.trim()) e.consigneeName = 'Requerido'

  // V3: phone validation — operadora required. DEBE matchear el backend
  // (^\+\d{1,3} \d{9,15}$): con +58 fijo, los dígitos locales van de 9 a 15.
  //   - Mobile: 10 dígitos sin leading 0 (ej "4141234567" = 414 + 1234567)
  //   - Mobile legacy: 11 dígitos con leading 0 (ej "04141234567")
  //   - FIJO: leading 0 + 8-10 dígitos = 9-11 total (ej "02121234567"). Un FIJO
  //     de 8 dígitos lo rechaza el backend, así que acá también.
  const phoneRaw = (g.consigneePhone || '').trim()
  if (!phoneRaw) {
    e.consigneePhone = 'Teléfono requerido'
  } else if (!/^\d+$/.test(phoneRaw)) {
    e.consigneePhone = 'Solo dígitos permitidos'
  } else if (phoneRaw.startsWith('0')) {
    if (phoneRaw.length < 9 || phoneRaw.length > 11) {
      e.consigneePhone =
        'Número FIJO debe tener entre 9 y 11 dígitos (incluí el 0 inicial)'
    }
  } else if (phoneRaw.length === 7) {
    e.consigneePhone = 'Operadora requerida'
  } else if (phoneRaw.length !== 10) {
    e.consigneePhone =
      phoneRaw.length < 10
        ? 'Número incompleto (operadora + 7 dígitos)'
        : 'Teléfono demasiado largo'
  }
  // Red de seguridad: paridad exacta con el regex del backend, por si alguna rama
  // anterior dejara pasar un valor que el backend rechazaría (evita el 500 al enviar).
  if (!e.consigneePhone && phoneRaw && !isBackendValidPhone(phoneRaw)) {
    e.consigneePhone = 'Teléfono inválido para el sistema (9 a 15 dígitos)'
  }

  if (
    g.consigneeEmail?.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(g.consigneeEmail.trim())
  )
    e.consigneeEmail = 'Formato inválido'

  if (!g.destinationAddress?.trim()) e.destinationAddress = 'Requerido'

  if (g.latitude != null || g.longitude != null) {
    if (!isValidCoord(g.latitude, -90, 90))
      e.latitude = 'Latitud inválida (-90 a 90)'
    if (!isValidCoord(g.longitude, -180, 180))
      e.longitude = 'Longitud inválida (-180 a 180)'
  }

  // Piezas: además de exigir al menos una, validamos CADA pieza (peso, dimensiones,
  // valor declarado —incluido el mínimo por peso+servicio—, categoría, cantidad). El
  // mínimo se valida POR PIEZA (cada pieza/categoría puede tener un valor distinto), por
  // eso le pasamos el contexto a validatePiece. Si alguna pieza falla, se marca la fila
  // para que no se pueda enviar (el detalle por pieza se ve al expandir).
  const requireDeclaredValue =
    isCOD ||
    (g.serviceId != null && (valueDeclareServiceIds?.has(g.serviceId) ?? false))
  if (!g.pieces?.length) {
    e.pieces = 'Debe tener al menos una pieza'
  } else if (
    g.pieces.some(
      (p) =>
        Object.keys(
          validatePiece(p, {
            rangeDeclareList,
            serviceId: g.serviceId,
            requireDeclaredValue,
          })
        ).length > 0
    )
  ) {
    // Sin texto: el detalle se muestra POR PIEZA en cada card. e.pieces queda como cadena
    // vacía (no se renderiza, pero mantiene la guía marcada/bloqueada en hasAnyError).
    e.pieces = ''
  }

  return e
}

export interface ValidatePieceContext {
  rangeDeclareList: RangeDeclareDto[]
  serviceId?: number
  requireDeclaredValue?: boolean
}

export function validatePiece(
  p: ShipmentPieceRow,
  ctx?: ValidatePieceContext
): Partial<Record<keyof ShipmentPieceRow, string>> {
  const e: Partial<Record<keyof ShipmentPieceRow, string>> = {}
  if (!p.quantity || p.quantity < 1) e.quantity = 'Debe ser al menos 1'
  if (!p.weight || p.weight <= 0) e.weight = 'Debe ser mayor a 0'
  if (!p.height || p.height <= 0) e.height = 'Debe ser mayor a 0'
  if (!p.width || p.width <= 0) e.width = 'Debe ser mayor a 0'
  if (!p.length || p.length <= 0) e.length = 'Debe ser mayor a 0'
  // Paridad con shipments/create: el valor declarado (y su mínimo) SOLO se exige cuando la
  // guía viaja asegurada — COD (siempre) o servicio con isValueDeclare. Si no aplica, el
  // operador puede dejar 0 y no se valida el mínimo. Default true por retrocompatibilidad.
  const requireDeclaredValue = ctx?.requireDeclaredValue ?? true
  if (requireDeclaredValue) {
    if (!p.declaredValue || p.declaredValue <= 0) {
      e.declaredValue = 'Debe ser mayor a 0'
    } else if (ctx?.rangeDeclareList) {
      // Mismo criterio que la creación de guía individual: el rango se busca por el peso
      // FACTURABLE de la línea (peso × cantidad) y devuelve el mínimo TOTAL. Como el campo es
      // por pieza, dividimos ese total entre la cantidad para indicarle cuánto poner por pieza
      // (redondeado hacia arriba al centavo, para que el valor mostrado siempre cumpla).
      const qty = p.quantity && p.quantity > 0 ? p.quantity : 1
      const totalMin = minDeclaredValue(
        (p.weight || 0) * qty,
        ctx.rangeDeclareList,
        ctx.serviceId
      )
      if (totalMin > 0) {
        const perPieceMin = Math.ceil((totalMin / qty) * 100) / 100
        if (p.declaredValue < perPieceMin)
          e.declaredValue = `El valor declarado por pieza debe ser al menos ${perPieceMin.toFixed(
            2
          )}$ (mínimo total ${totalMin.toFixed(2)}$ para ${qty} pieza(s)).`
      }
    }
  }
  if (!p.categoryId) e.categoryId = 'Requerido'
  return e
}
